import * as detect from 'detect-browser';
import { isNil } from 'lodash';

type DetectedClient = ReturnType<typeof detect.detect>;

const MIN_IOS_MAJOR_VERSION = 16;

const IOS_UA_PATTERNS: RegExp[] = [
  /(?:iPhone|iPad|iPod).*?OS (\d+)[_.]/i,
  /CPU (?:iPhone )?OS (\d+)[_.]/i,
  /(?:iPhone|iPad|iPod).*?Version\/(\d+)/i,
  /Version\/(\d+)[.\d]* Mobile/i,
  /OS (\d+)[_.]\d+ like Mac OS X/i
];

/**
 * Версия iOS из User-Agent (надёжнее, чем detect-browser.version на iPhone/iPad и в DevTools).
 */
export function resolveIosMajorVersionFromUserAgent(userAgent: string): number | null {
  const majors: number[] = [];

  for (const pattern of IOS_UA_PATTERNS) {
    const match = userAgent.match(pattern);
    if (!match?.[1]) {
      continue;
    }
    const major = parseInt(match[1], 10);
    if (!Number.isNaN(major)) {
      majors.push(major);
    }
  }

  if (!majors.length) {
    return null;
  }

  return Math.max(...majors);
}

export function isAppleMobileUserAgent(userAgent: string): boolean {
  return /iPhone|iPad|iPod/i.test(userAgent);
}

/**
 * Мажорная версия Safari/iOS для проверки совместимости.
 * На iPhone/iPad — только UA; на десктопном Safari — detect-browser.
 */
export function resolveSafariIosMajorVersion(browser: DetectedClient, userAgent: string): number | null {
  if (!browser || browser.type !== 'browser' || (browser.name !== 'safari' && browser.name !== 'ios')) {
    return null;
  }

  if (isAppleMobileUserAgent(userAgent)) {
    return resolveIosMajorVersionFromUserAgent(userAgent);
  }

  const fromDetect = browser.version?.split('.')[0];
  if (!fromDetect) {
    return null;
  }

  const major = parseInt(fromDetect, 10);
  return Number.isNaN(major) ? null : major;
}

export function isUnsupportedSafariOrIos(browser: DetectedClient, userAgent: string): boolean {
  if (isAppleMobileUserAgent(userAgent)) {
    const major = resolveIosMajorVersionFromUserAgent(userAgent);
    if (isNil(major)) {
      return false;
    }
    return major < MIN_IOS_MAJOR_VERSION;
  }

  const major = resolveSafariIosMajorVersion(browser, userAgent);
  if (isNil(major)) {
    return false;
  }
  return major < MIN_IOS_MAJOR_VERSION;
}

export const MIN_SUPPORTED_IOS_MAJOR_VERSION = MIN_IOS_MAJOR_VERSION;
